import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { statusOptions, releaseStatusOptions } from '../mock';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Badge } from './ui/badge';
import { Apple, Youtube, Music, Link2, Send, Trash2, ExternalLink, Copy, Instagram, Linkedin, Twitter, Eye, Download, FileSignature, UserPlus, Search, Loader2, MessageSquare, CalendarPlus, CalendarCheck } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { TagInput } from './TagInput';
import ImageDropzone from './ImageDropzone';
import { TalkingPointsPanel } from './TalkingPointsPanel';
import EmailHistoryList from './EmailHistoryList';
import { Release, Guests as GuestsApi } from '../lib/api';
import ReleaseSubmissionDialog from './ReleaseSubmissionDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { SOURCES } from '../lib/prospectConfig';

export default function GuestDetailSheet({ guestId, open, onOpenChange, onCompose }) {
  const { guests, updateGuest, removeGuest, activeShow, signRelease, setActiveTable, refreshGuests } = useApp();
  const guest = guests.find(g => g.id === guestId);
  const [tab, setTab] = useState('details');
  const [submission, setSubmission] = useState(null);
  const [showSubmission, setShowSubmission] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteSource, setPromoteSource] = useState('other');
  const [promoteSourceDetail, setPromoteSourceDetail] = useState('');
  const [findingPlatforms, setFindingPlatforms] = useState(false);
  const [syncingCal, setSyncingCal] = useState(false);

  const syncCalendar = async () => {
    if (!guest) return;
    setSyncingCal(true);
    try {
      const r = await GuestsApi.syncCalendar(guest.id);
      toast({
        title: r.action === 'created' ? 'Added to Outlook calendar' : 'Outlook event updated',
        description: r.allDay ? 'Created as an all-day event — set a time on the recording date for a timed slot.' : undefined,
      });
      await refreshGuests();
    } catch (e) {
      toast({ title: 'Calendar sync failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSyncingCal(false);
    }
  };

  const findPlatforms = async () => {
    if (!guest) return;
    setFindingPlatforms(true);
    try {
      const r = await GuestsApi.findPlatforms(guest.id, false);
      const applied = r.applied || {};
      const appliedKeys = Object.keys(applied);

      // Reusable disclosure showing exactly which Serper queries we ran.
      const renderDetails = () => {
        const tried = r.queriesTried || {};
        const platforms = Object.keys(tried);
        const sig = r.channelSignature || {};
        const sigKeys = Object.keys(sig).filter(k => (sig[k] || []).length);
        if (!platforms.length && !(r.errors || []).length && !sigKeys.length) return null;
        return (
          <details className="mt-2 text-xs opacity-80" data-testid="find-platforms-search-details">
            <summary className="cursor-pointer underline">Show search details</summary>
            <div className="mt-2 space-y-2">
              {sigKeys.length > 0 && (
                <div>
                  <div className="font-medium">Show channel signature (auto-learned from existing guests)</div>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {sigKeys.map(k => (
                      <li key={k}><span className="capitalize">{k}</span>: {sig[k].join(', ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              {platforms.map(p => (
                <div key={p}>
                  <div className="font-medium capitalize">{p}</div>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {(tried[p] || []).map((q, i) => (
                      <li key={i} className="font-mono break-all">{q}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {(r.errors || []).length > 0 && (
                <div>
                  <div className="font-medium text-red-600">Serper errors</div>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {r.errors.map((e, i) => (<li key={i} className="break-all">{e}</li>))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        );
      };

      if (appliedKeys.length === 0) {
        const websiteHit = r.found?.website ? ` · also found on show site: ${r.found.website}` : '';
        const tried = r.queriesRun ? ` (ran ${r.queriesRun} queries)` : '';
        const rejectedCount = Object.values(r.rejected || {}).reduce((s, n) => s + n, 0);
        const rejectedNote = rejectedCount ? ` Rejected ${rejectedCount} result${rejectedCount === 1 ? '' : 's'} that belonged to a different show or person.` : '';
        const hadExisting = Object.values(r.platforms || {}).some(v => (v || '').toString().trim());
        const msg = r.errors?.length
          ? 'Serper returned errors — see details below.'
          : `Nothing matched${tried}.${rejectedNote}${hadExisting ? ' Existing links kept.' : ' No links saved.'}${websiteHit}`;
        toast({
          title: 'No new platform links applied',
          description: (
            <div>
              <div>{msg}</div>
              {renderDetails()}
            </div>
          ),
          duration: 12000,
        });
      } else {
        toast({
          title: `Added ${appliedKeys.length} platform link${appliedKeys.length === 1 ? '' : 's'}`,
          description: (
            <div>
              <div>{appliedKeys.map(k => `${k}: ${applied[k]}`).join(' · ').slice(0, 220)}</div>
              {renderDetails()}
            </div>
          ),
          duration: 10000,
        });
        await refreshGuests();
      }
    } catch (e) {
      toast({ title: 'Auto-find failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setFindingPlatforms(false);
    }
  };

  const openPromoteDialog = () => {
    setPromoteSource('other');
    setPromoteSourceDetail('');
    setPromoteOpen(true);
  };

  const promoteToProspect = async () => {
    if (!guest) return;
    setPromoting(true);
    try {
      await GuestsApi.promoteToProspect(guest.id, {
        source: promoteSource,
        sourceDetail: promoteSourceDetail || undefined,
      });
      toast({ title: 'Promoted to Prospect', description: `${guest.name} is now in the Prospects pipeline.` });
      setPromoteOpen(false);
      onOpenChange(false);
      await refreshGuests();
      setActiveTable('prospects');
    } catch (e) {
      toast({ title: 'Promotion failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setPromoting(false);
    }
  };

  useEffect(() => {
    if (guest && guest.releaseStatus === 'signed') {
      Release.listSubmissions(undefined, guest.id).then(items => setSubmission(items[0] || null)).catch(() => setSubmission(null));
    } else {
      setSubmission(null);
    }
  }, [guest?.id, guest?.releaseStatus]);

  if (!guest) return null;

  const set = (patch) => updateGuest(guest.id, patch);
  const setPlatform = (key, value) => set({ platforms: { ...guest.platforms, [key]: value } });
  const setSocial = (key, value) => set({ socials: { ...guest.socials, [key]: value } });

  const releaseLink = `${window.location.origin}/release/${guest.id}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto p-0">
        <div className="p-5 border-b border-stone-200" style={{ background: `linear-gradient(135deg, ${activeShow.color}, ${activeShow.color}dd)` }}>
          <SheetHeader>
            <SheetTitle className="text-white">
              <div className="flex items-center gap-3">
                {guest.avatar ? <img src={guest.avatar} className="w-14 h-14 rounded-full object-cover border-2 border-white" alt="" /> :
                  <div className="w-14 h-14 rounded-full bg-white/20 grid place-items-center text-white text-xl font-bold border-2 border-white">{guest.name[0]}</div>}
                <div>
                  <div className="text-[20px] font-serif">{guest.name}</div>
                  <div className="text-[12.5px] opacity-85 font-normal">{guest.role} • {guest.company}</div>
                </div>
              </div>
            </SheetTitle>
          </SheetHeader>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="px-5 pt-3">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="prep" data-testid="tab-prep">Prep{guest.talkingPoints?.url ? <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500 inline-block" /> : null}</TabsTrigger>
            <TabsTrigger value="release">Release</TabsTrigger>
            <TabsTrigger value="platforms">Platforms</TabsTrigger>
            <TabsTrigger value="checklist">Checklist</TabsTrigger>
            <TabsTrigger value="assets">Assets</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Name</Label>
                <Input data-testid="guest-name-input" value={guest.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Email</Label>
                <Input data-testid="guest-email-input" value={guest.email} onChange={(e) => set({ email: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Cell phone</Label>
                <Input type="tel" data-testid="guest-phone-input" value={guest.phone || ''} onChange={(e) => set({ phone: e.target.value })} placeholder="+1 555 123 4567" />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Company</Label>
                <Input value={guest.company || ''} onChange={(e) => set({ company: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Role</Label>
                <Input data-testid="guest-role-input" value={guest.role || ''} onChange={(e) => set({ role: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase text-stone-500">Location</Label>
                <Input value={guest.location || ''} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Tbilisi, Georgia · Napa, CA · London, UK" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase text-stone-500">Episode title</Label>
                <Input value={guest.episodeTitle || ''} onChange={(e) => set({ episodeTitle: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Air date</Label>
                <Input type="date" value={guest.episodeDate || ''} onChange={(e) => set({ episodeDate: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Recording date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={(guest.recordingDate || '').slice(0, 16)}
                  onChange={(e) => set({ recordingDate: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Episode status</Label>
                <Select value={guest.status} onValueChange={(v) => set({ status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase text-stone-500">Interested parties (cc'd on emails)</Label>
                <TagInput
                  dataTestid="interested-parties-input"
                  values={guest.interestedParties || []}
                  onChange={(vals) => set({ interestedParties: vals })}
                  placeholder="Type an email and press Enter — add as many as you need"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-stone-200">
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-stone-900 flex items-center gap-1.5">
                  {guest.outlookEventId ? <CalendarCheck className="w-4 h-4 text-emerald-600" /> : <CalendarPlus className="w-4 h-4 text-stone-500" />}
                  Outlook calendar
                </div>
                <div className="text-[11px] text-stone-500">
                  {guest.outlookEventId
                    ? 'On your calendar — the event moves automatically when you change the recording date.'
                    : 'Not on your calendar yet. Confirmed guests with a recording date are added automatically.'}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                data-testid="sync-calendar-btn"
                disabled={syncingCal}
                onClick={syncCalendar}
                className="shrink-0"
              >
                {syncingCal ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />}
                {syncingCal ? 'Syncing…' : guest.outlookEventId ? 'Update event' : 'Add to calendar'}
              </Button>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Notes</Label>
              <Textarea rows={4} value={guest.notes || ''} onChange={(e) => set({ notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2.5 p-2.5 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50">
              <input
                type="checkbox"
                checked={guest.automationEnabled !== false}
                onChange={(e) => set({ automationEnabled: e.target.checked })}
                className="w-4 h-4 accent-stone-900"
              />
              <div className="flex-1">
                <div className="text-[12.5px] font-medium text-stone-900">Automations enabled</div>
                <div className="text-[11px] text-stone-500">When off, this guest is skipped by all auto-emails (reminders, upcoming, thank-you).</div>
              </div>
            </label>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Socials</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-stone-500" /><Input placeholder="linkedin" value={guest.socials?.linkedin || ''} onChange={(e) => setSocial('linkedin', e.target.value)} /></div>
                <div className="flex items-center gap-2"><Instagram className="w-4 h-4 text-stone-500" /><Input placeholder="instagram" value={guest.socials?.instagram || ''} onChange={(e) => setSocial('instagram', e.target.value)} /></div>
                <div className="flex items-center gap-2"><Twitter className="w-4 h-4 text-stone-500" /><Input placeholder="twitter" value={guest.socials?.twitter || ''} onChange={(e) => setSocial('twitter', e.target.value)} /></div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="prep" className="space-y-4 mt-4">
            <TalkingPointsPanel guest={guest} onChange={(val, kind) => set(kind === 'prompt' ? val : { talkingPoints: val })} />
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Notes</Label>
              <Textarea value={guest.notes || ''} onChange={(e) => set({ notes: e.target.value })} rows={6} placeholder="Quick notes, questions, pronunciation…" />
            </div>
          </TabsContent>

          <TabsContent value="release" className="space-y-4 mt-4">
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-[12.5px] text-amber-900">
              Submission of the release form constitutes acceptance of the terms and release.
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Status</Label>
              <Select value={guest.releaseStatus} onValueChange={(v) => set({ releaseStatus: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {releaseStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Signed on</Label>
              <Input type="date" value={guest.signedDate || ''} onChange={(e) => set({ signedDate: e.target.value })} />
            </div>
            <div className="p-3 rounded-md bg-stone-50 border border-stone-200">
              <div className="text-[11px] uppercase text-stone-500 mb-1">Public release link</div>
              <div className="flex items-center gap-2">
                <code className="text-[11.5px] text-stone-700 truncate flex-1">{releaseLink}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(releaseLink); toast({ title: 'Copied' }); }}><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => window.open(releaseLink, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => signRelease(guest.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Mark as signed</Button>
              <Button variant="outline" onClick={() => onCompose(guest.id, 't2')}>Send reminder</Button>
            </div>
            {submission && (
              <div className="mt-2 p-3 rounded-md border border-emerald-200 bg-emerald-50/40">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-emerald-600" />
                    <div>
                      <div className="text-[12.5px] font-medium text-stone-900">Signed by {submission.fullName}</div>
                      <div className="text-[11px] text-stone-500">{new Date(submission.submittedAt).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowSubmission(true)}>
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> View signature
                  </Button>
                  <a href={Release.pdfUrl(submission.id)} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="platforms" className="space-y-3 mt-4">
            <div className="flex items-center justify-between gap-2 -mb-1">
              <div className="text-[11px] uppercase text-stone-500">Episode platform links</div>
              <Button
                data-testid="find-platforms-btn"
                size="sm"
                variant="outline"
                disabled={findingPlatforms}
                onClick={findPlatforms}
                className="h-7 text-[11.5px]"
              >
                {findingPlatforms ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                {findingPlatforms ? 'Searching…' : 'Auto-find from web'}
              </Button>
            </div>
            {[
              { key: 'apple', label: 'Apple Podcasts', icon: Apple },
              { key: 'spotify', label: 'Spotify', icon: Music },
              { key: 'youtube', label: 'YouTube', icon: Youtube },
              { key: 'libsyn', label: `${activeShow.name} website page`, icon: Link2 }
            ].map(({ key, label, icon: Icon }) => (
              <div key={key}>
                <Label className="text-[11px] uppercase text-stone-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{label}</Label>
                <Input placeholder={`Paste ${label} URL`} value={guest.platforms?.[key] || ''} onChange={(e) => setPlatform(key, e.target.value)} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="checklist" className="space-y-2 mt-4">
            <div className="text-[12px] text-stone-500 mb-2">Publishing checklist for {guest.episodeTitle || 'this episode'}</div>
            {[
              { key: 'recordingDone', label: 'Recording complete' },
              { key: 'edited', label: 'Audio/video edited' },
              { key: 'thumbnail', label: 'Square thumbnail created' },
              { key: 'thumbnail16x9', label: '16:9 YouTube thumbnail created' },
              { key: 'showNotes', label: 'Show notes written' },
              { key: 'appleLink', label: 'Apple Podcasts link added' },
              { key: 'spotifyLink', label: 'Spotify link added' },
              { key: 'youtubeLink', label: 'YouTube link added' },
              { key: 'socialPosts', label: 'Social posts scheduled' },
              { key: 'guestNotified', label: 'Guest notified episode is live' }
            ].map(item => {
              const checked = !!(guest.checklist || {})[item.key];
              return (
                <label key={item.key} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-stone-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => set({ checklist: { ...(guest.checklist || {}), [item.key]: e.target.checked } })}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className={`text-[13px] ${checked ? 'text-stone-400 line-through' : 'text-stone-900'}`}>{item.label}</span>
                </label>
              );
            })}
            <div className="text-[11.5px] text-stone-500 pt-2 border-t mt-2">
              {(() => {
                const items = guest.checklist || {};
                const total = 10;
                const done = Object.values(items).filter(Boolean).length;
                return `${done} of ${total} complete (${Math.round(done / total * 100)}%)`;
              })()}
            </div>
          </TabsContent>

          <TabsContent value="assets" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-stone-500 block mb-1.5">Square thumbnail</Label>
                <ImageDropzone value={guest.thumbnail} onChange={(url) => set({ thumbnail: url })} aspect="square" label="Drop square image" />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500 block mb-1.5">16:9 YouTube thumbnail</Label>
                <ImageDropzone value={guest.thumbnail16x9} onChange={(url) => set({ thumbnail16x9: url })} aspect="video" label="Drop 16:9 image" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500 block mb-1.5">Avatar / headshot</Label>
              <div className="w-32"><ImageDropzone value={guest.avatar} onChange={(url) => set({ avatar: url })} aspect="square" label="Drop headshot" /></div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Tags (comma separated)</Label>
              <Input value={(guest.tags || []).join(', ')} onChange={(e) => set({ tags: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(guest.tags || []).map(t => <Badge key={t} variant="outline">{t}</Badge>)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="emails" className="space-y-2 mt-4">
            {tab === 'emails' && (
              <EmailHistoryList fetcher={() => GuestsApi.listEmails(guest.id)} dataTestid="guest-email-history" />
            )}
          </TabsContent>
        </Tabs>

        <div className="px-5 py-4 border-t border-stone-200 mt-4 flex items-center justify-between sticky bottom-0 bg-white">
          <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => { removeGuest(guest.id); onOpenChange(false); toast({ title: 'Guest deleted' }); }}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
          <div className="flex items-center gap-2">
            {guest.status === 'potential' && (
              <Button data-testid="promote-to-prospect-btn" variant="outline" onClick={openPromoteDialog} disabled={promoting} className="border-violet-300 text-violet-700 hover:bg-violet-50">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {promoting ? 'Promoting…' : 'Promote to Prospect'}
              </Button>
            )}
            {(guest.phone || '').trim() && (
              <Button asChild variant="outline" data-testid="text-guest-btn" className="border-stone-300">
                <a
                  href={`sms:${(guest.phone || '').replace(/\s+/g, '')}?&body=${encodeURIComponent(`Hi ${(guest.name || '').split(' ')[0] || 'there'}, `)}`}
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Text
                </a>
              </Button>
            )}
            <Button onClick={() => onCompose(guest.id)} className="text-white" style={{ background: activeShow.color }}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send email
            </Button>
          </div>
        </div>
      </SheetContent>
      <ReleaseSubmissionDialog submission={submission} open={showSubmission} onOpenChange={setShowSubmission} />
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Promote to Prospect</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px]">
            <div className="text-stone-700">
              Move <b>{guest.name}</b> into the Prospects pipeline. They will be removed from Guests.
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Source of guest</Label>
              <Select value={promoteSource} onValueChange={setPromoteSource}>
                <SelectTrigger data-testid="promote-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: s.color }} />
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-stone-500 mt-1">Used for Source-of-Booking analytics in the Prospects view.</div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Source detail (optional)</Label>
              <Input
                data-testid="promote-source-detail-input"
                value={promoteSourceDetail}
                onChange={(e) => setPromoteSourceDetail(e.target.value)}
                placeholder="e.g. article URL, referrer name, conference name"
              />
              <div className="text-[11px] text-stone-500 mt-1">Will appear in <code>{'{{referrer}}'}</code> / <code>{'{{topic}}'}</code> merge fields when you draft outreach.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteOpen(false)}>Cancel</Button>
            <Button data-testid="promote-confirm-btn" onClick={promoteToProspect} disabled={promoting} className="bg-violet-600 hover:bg-violet-700 text-white">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {promoting ? 'Promoting…' : 'Confirm promote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
